import { useState, useEffect, useMemo } from 'react';
import { BotMap, IBotData, IManualMovement } from '../shared/types';
import { deleteMovement, updateMovement } from '../api/movements';
import { useApiData } from './useApiData';
import { ReconnectingWebSocket } from '../socket'; // Updated import

const HIDDEN_BOT_FAMILIES = ['ETH_QNT_USDT', 'ETH_USDC_FET'];

// Share exclusions between navigation and the bots used by Summary cards and totals.
const isExcludedBot = (botId: string) => {
    const lower = botId.toLowerCase();
    const family = botId.replace(/_\d+$/, '');
    return lower === 'no_bot' || lower.includes('test') || lower.includes('dipster') || lower.includes('dumper') ||
        HIDDEN_BOT_FAMILIES.includes(family);
};

export function useDashboardData(socket: ReconnectingWebSocket) { // Accept socket as prop
    const { data, loading, error, refreshData, isDataEmpty } = useApiData(socket); // Pass socket to useApiData

    const [selectedBot, setSelectedBot] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        if (selectedBot === null) return; // ← no hagas nada si venís de "Home"
        if (!selectedBot && data?.botsData) {
            const first = Object.keys(data.botsData).sort()[0];
            setSelectedBot(first);
        }
    }, [data, selectedBot, refreshData]);

    const isDisabledBot = (bot?: IBotData) => bot?.stats.enabled === false;

    const groupedBotsByChain = useMemo(() => {
        const result: Record<string, Set<string>> = {};

        Object.keys(data?.botsData ?? {})
            .filter(botId => !isExcludedBot(botId) && !isDisabledBot(data?.botsData[botId]))
            .forEach(botId => {
                const [chain] = botId.split('_');
                const family = botId.replace(/_\d+$/, '');
                if (!result[chain]) result[chain] = new Set();
                result[chain].add(family);
            });

        return Object.keys(result)
            .sort()
            .reduce<Record<string, string[]>>((acc, chain) => {
                acc[chain] = Array.from(result[chain]).sort();
                return acc;
            }, {});
    }, [data]);

    const groupedBots = useMemo(() => {
        const result: Record<string, string[]> = {};
        if (!data) return result;
        for (const botId of Object.keys(data.botsData)) {
            if (isExcludedBot(botId) || isDisabledBot(data.botsData[botId])) continue;
            const family = botId.replace(/_\d+$/, '');
            if (!result[family]) result[family] = [];
            result[family].push(botId);
        }
        return result;
    }, [data]);

    const selectedFamily = useMemo(() => {
        if (!selectedBot) return null;
        return selectedBot.replace(/_\d+$/, '');
    }, [selectedBot]);

    const botGroup = useMemo(() => selectedFamily ? groupedBots[selectedFamily] || [] : [], [selectedFamily, groupedBots]);

    const botsToDisplay = useMemo(() => {
        if (!data || !selectedBot) return [];
        return botGroup.map(id => data.botsData[id]).filter(Boolean);
    }, [data, botGroup, selectedBot]);

    const currentDecimals = botsToDisplay[0]?.stats.decimals ?? 18;

    const allBots = useMemo((): IBotData[] => {
        return data?.botsData 
            ? Object.values(data.botsData).filter(bot => !isExcludedBot(bot.stats.botName) && !isDisabledBot(bot))
            : [];
    }, [data]);

    const botMap: BotMap = useMemo(() => Object.fromEntries(allBots.map(b => [
        b.stats.botName,
        {
            id: b.stats.botName,
            name: b.stats.botName,
            chain: b.stats.botName.split('_')[0],
            token0: b.stats.token0Symbol,
            token1: b.stats.token1Symbol,
            poolAddress: b.stats.poolAddress,
            walletAddress: b.stats.walletAddress,
            enabled: b.stats.enabled,
        }
    ])), [allBots]);

    const botTokens = useMemo(() => {
        const tokenMap: Record<string, string[]> = {};
        for (const bot of allBots) {
            tokenMap[bot.stats.botName] = [bot.stats.token0Symbol, bot.stats.token1Symbol];
        }
        return tokenMap;
    }, [allBots]);

    const handleDataRefresh = () => refreshData();

    const onDeleteMovement = async (id: string) => {
        await deleteMovement(socket, id);
        handleDataRefresh();
    };

    const onUpdateMovement = (movement: IManualMovement) => {
        updateMovement(socket, movement);
        handleDataRefresh();
    };

    const buybacks = useMemo(() => {
        // Read from ALL botsData entries — allBots excludes DIPSTER/DUMPER which have no LP positions
        const allBuybacks = Object.values(data?.botsData ?? {}).flatMap((b: any) => b.buybacks || []);
        return {
            completed: allBuybacks.filter((b: any) => b.status === 'COMPLETED'),
            pending: allBuybacks.filter((b: any) => b.status !== 'COMPLETED'),
        };
    }, [data]);

    const tradingActivityBuybacks = useMemo(() => data?.tradingActivityBuybacks ?? buybacks, [data?.tradingActivityBuybacks, buybacks]);

    return {
        data,
        loading,
        error,
        selectedBot,
        setSelectedBot,
        isModalOpen,
        setIsModalOpen,
        botsToDisplay,
        groupedBots,
        botFamilies: Object.keys(groupedBots).sort(),
        currentDecimals,
        onDeleteMovement,
        onUpdateMovement,
        handleDataRefresh,
        allBots,
        groupedBotsByChain,
        botMap,
        botTokens,
        buybacks,
        tradingActivityBuybacks,
        isDataEmpty,
    };
}
